import { action, observable, toJS } from 'mobx';

import createContext from 'bz-utilities/hooks/context-v2';

import { bind } from 'bind-decorator';
import _ from 'lodash';

export interface IOrderable {
  position?: number;
}

export enum EDirection {
  down,
  up
}

class MenuStore {
  @observable pages = observable.map<string, MenuPage>(new Map());

  @action.bound
  onLoad(menu?: Map<string, any>) {
    const entries = Object.entries(menu ?? new Map());

    entries.forEach(([key, value]) => {
      const page = new MenuPage(value, key);
      this.pages.set(key, page);
    });

    entries.forEach(([key, value]) => {
      Object.keys(value).forEach((nestedKey) => {
        if (!this.pages.has(nestedKey)) {
          this.pages.set(nestedKey, new MenuPage({}, nestedKey));
        }
      });
    });
  }

  @bind
  toCamelCaseWithCharacters(input: string): string {
    return input
      ?.split(' ')
      .map((word, index) => {
        return index === 0 ? word.toLowerCase() : word[0].toUpperCase() + word.slice(1);
      })
      .join('');
  }

  @action.bound
  onAdd(route: string, description: string) {
    const key = `${route}.${this.toCamelCaseWithCharacters(description)}`;
    this.pages.set(key, new MenuPage({}, key));

    const page = this.pages.get(route);
    page?.order?.push(key);
    page?.items?.set(key, new MenuPageItem(description));
  }

  @action.bound
  onDelete(route: string, parent: string) {
    const _parent = this.pages.get(parent);
    _parent?.order?.remove(route);

    this.pages.delete(route);
    _parent?.items?.delete(route);
  }

  @action.bound
  onExtract(): Record<string, Record<string, IMenuPage>> {
    const result: Record<string, Record<string, IMenuPage>> = {};

    Array.from(this.pages.entries()).map(([key, value]) => {
      result[key] = value.onExtract();
    });

    return result;
  }
}

export class MenuPage {
  @observable route = '';

  @observable order = observable.array<string>([]);
  @observable items = observable.map<string, MenuPageItem>(new Map());

  constructor(page: Record<string, IMenuPage | string>, route: string) {
    let _page = page || {};

    this.route = route;

    const keys = Object.entries(_page)
      .map(([key, value]) => {
        return { key: key, pos: value.position || 0 };
      })
      .sort((a, b) => {
        if (a.pos < b.pos) {
          return -1;
        }
        if (a.pos > b.pos) {
          return 1;
        }
        return 0;
      })
      .map((x) => x.key);

    keys.map((key) => {
      this.items.set(key, new MenuPageItem(_page[key]));
    });

    this.order.replace(keys);
  }

  @action.bound
  onReorder(key: string, dir: EDirection) {
    let idx = this.order.indexOf(key);

    let pos =
      dir === EDirection.up ? Math.max(0, idx - 1) : Math.min(this.order.length - 1, idx + 1);

    this.order.remove(key);
    this.order.splice(pos, 0, key);
  }

  @action.bound
  onExtract(): Record<string, IMenuPage> {
    const result: Record<string, IMenuPage> = {};

    Array.from(this.items.entries()).map(([key, value]) => {
      result[key] = {
        ...toJS(value),
        position: this.order.indexOf(key)
      };
    });

    return result;
  }
}

export interface IMenuPage {
  description: string;
  alternates: object;
  position: number;
}

export class MenuPageItem {
  @observable description = '';
  @observable alternates = observable.map(new Map());

  constructor(item: string | IMenuPage) {
    if (typeof item === 'object') {
      this.description = item.description ?? '';
      this.alternates.replace(item.alternates);
    } else {
      this.description = item;
    }
  }
}

export const [AlternatesContext, useAlternates] = createContext<Map<string, any>>('Alternates');
export const [MenuStoreContext, useMenuStore] = createContext<MenuStore>('MenuStore');
export const [MenuPageContext, useMenuPage] = createContext<MenuPage>('MenuPage');
export const [ParentMenuPageContext, useParentMenuPage] = createContext<MenuPage>('ParentMenuPage');
export const [MenuPageItemContext, useMenuPageItem] = createContext<MenuPageItem>('MenuPageItem');

export default MenuStore;
